import type { DamageType } from '../types/primitives'

export type DamageMitigationKind = 'NORMAL' | 'RESISTED' | 'VULNERABLE' | 'IMMUNE'

export interface ResolveDamageInput {
  rawDamage: number
  damageType: DamageType
  /** Combined type multipliers from armor profile + enemy traits. */
  typeMultiplier: number
  /**
   * Flat armor value. M42 ACTIVE content uses 0 (identity).
   * Provisional formula: mitigated = raw * typeMult * armorFactor(armorValue)
   */
  armorValue: number
}

export interface ResolveDamageResult {
  finalDamage: number
  typeMultiplier: number
  armorFactor: number
  mitigationKind: DamageMitigationKind
  /** True when typeMultiplier === 0 (explicit immunity, not merely zero raw). */
  wasImmune: boolean
}

/** Validate multiplier: finite, >= 0. Rejects negatives. */
export function clampDamageMultiplier(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 1
  return value
}

/**
 * Provisional flat armor factor. armorValue 0 → 1 (identity).
 * Does NOT alter M41 combat (all current armorValue = 0).
 */
export function armorMitigationFactor(armorValue: number): number {
  if (!Number.isFinite(armorValue) || armorValue <= 0) return 1
  // Soft provisional curve — unused by ACTIVE content.
  return 100 / (100 + armorValue)
}

export function classifyMitigation(typeMultiplier: number): DamageMitigationKind {
  if (typeMultiplier <= 0) return 'IMMUNE'
  if (typeMultiplier < 1) return 'RESISTED'
  if (typeMultiplier > 1) return 'VULNERABLE'
  return 'NORMAL'
}

/**
 * Pure damage resolution path (pre-shield / pre-HP).
 *
 * Pipeline (documented):
 *   rawDamage → type modifier → armor mitigation → (caller: shield → HP)
 *
 * Current SHIELD-before-HP ordering is preserved in GameSession.applyDamage.
 */
export function resolveDamage(input: ResolveDamageInput): ResolveDamageResult {
  const raw = Math.max(0, Number.isFinite(input.rawDamage) ? input.rawDamage : 0)
  const typeMultiplier = clampDamageMultiplier(input.typeMultiplier)
  const armorFactor = armorMitigationFactor(input.armorValue)
  const wasImmune = typeMultiplier <= 0
  const finalDamage = wasImmune ? 0 : raw * typeMultiplier * armorFactor
  return {
    finalDamage,
    typeMultiplier,
    armorFactor,
    mitigationKind: classifyMitigation(typeMultiplier),
    wasImmune
  }
}

export function resolveTypeMultiplier(
  damageType: DamageType,
  modifiers?: Partial<Record<DamageType, number>>
): number {
  if (!modifiers) return 1
  const v = modifiers[damageType]
  if (v === undefined) return 1
  return clampDamageMultiplier(v)
}
